/**
 * Service de gestion des restrictions utilisateur (côté utilisateur)
 * 
 * Ce service est utilisé par les pages utilisateur pour vérifier
 * leurs restrictions et statut de bannissement.
 */

import api from './api'; // ✅ Utiliser l'instance API avec intercepteurs

const shouldFallbackToPublicUsersRoute = (error) => {
  const status = error?.response?.status;
  return status === 404;
};

const getWithFallback = async (primaryPath, fallbackPath, config) => {
  try {
    return await api.get(primaryPath, config);
  } catch (error) {
    if (fallbackPath && shouldFallbackToPublicUsersRoute(error)) {
      return await api.get(fallbackPath, config);
    }
    throw error;
  }
};

/**
 * Types de restrictions avec labels utilisateur
 */
export const RESTRICTION_TYPES = {
  BOOKING_BLOCK: 'booking_block',
  LISTING_UNPUBLISH: 'listing_unpublish',
  LISTING_BLOCK: 'listing_block',
  MESSAGE_BLOCK: 'message_block',
  REVIEW_BLOCK: 'review_block',
  PAYOUT_HOLD: 'payout_hold',
  PAYIN_BLOCK: 'payin_block',
  FEATURE_BLOCK: 'feature_block',
  KYC_HOLD: 'kyc_hold',
  SEARCH_DELIST: 'search_delist',
  BAN_TEMPORARY: 'ban_temporary',
  BAN_PERMANENT: 'ban_permanent',
  PROBATION: 'probation'
};

export const RESTRICTION_LABELS = {
  [RESTRICTION_TYPES.BOOKING_BLOCK]: 'Réservations bloquées',
  [RESTRICTION_TYPES.LISTING_UNPUBLISH]: 'Annonces dépubliées',
  [RESTRICTION_TYPES.LISTING_BLOCK]: 'Création d\'annonces bloquée',
  [RESTRICTION_TYPES.MESSAGE_BLOCK]: 'Messagerie bloquée',
  [RESTRICTION_TYPES.REVIEW_BLOCK]: 'Avis bloqués',
  [RESTRICTION_TYPES.PAYOUT_HOLD]: 'Versements en attente',
  [RESTRICTION_TYPES.PAYIN_BLOCK]: 'Paiements bloqués',
  [RESTRICTION_TYPES.FEATURE_BLOCK]: 'Fonctionnalité bloquée',
  [RESTRICTION_TYPES.KYC_HOLD]: 'Vérification requise',
  [RESTRICTION_TYPES.SEARCH_DELIST]: 'Visibilité réduite',
  [RESTRICTION_TYPES.BAN_TEMPORARY]: 'Compte suspendu',
  [RESTRICTION_TYPES.BAN_PERMANENT]: 'Compte fermé',
  [RESTRICTION_TYPES.PROBATION]: 'Période probatoire'
};

export const RESTRICTION_DESCRIPTIONS = {
  [RESTRICTION_TYPES.BOOKING_BLOCK]: 'Vous ne pouvez pas effectuer de nouvelles réservations pour le moment.',
  [RESTRICTION_TYPES.LISTING_UNPUBLISH]: 'Vos annonces ont été temporairement dépubliées.',
  [RESTRICTION_TYPES.LISTING_BLOCK]: 'Vous ne pouvez pas créer de nouvelles annonces pour le moment.',
  [RESTRICTION_TYPES.MESSAGE_BLOCK]: 'Vous ne pouvez pas envoyer de messages pour le moment.',
  [RESTRICTION_TYPES.REVIEW_BLOCK]: 'Vous ne pouvez pas laisser ou modifier d\'avis pour le moment.',
  [RESTRICTION_TYPES.PAYOUT_HOLD]: 'Vos versements sont temporairement en attente.',
  [RESTRICTION_TYPES.PAYIN_BLOCK]: 'Vous ne pouvez pas effectuer de paiements pour le moment.',
  [RESTRICTION_TYPES.FEATURE_BLOCK]: 'Cette fonctionnalité est temporairement indisponible pour votre compte.',
  [RESTRICTION_TYPES.KYC_HOLD]: 'Vous devez compléter la vérification de votre identité.',
  [RESTRICTION_TYPES.SEARCH_DELIST]: 'Vos annonces peuvent ne pas apparaître dans les résultats de recherche.',
  [RESTRICTION_TYPES.BAN_TEMPORARY]: 'Votre compte est temporairement suspendu.',
  [RESTRICTION_TYPES.BAN_PERMANENT]: 'Votre compte a été fermé définitivement.',
  [RESTRICTION_TYPES.PROBATION]: 'Votre compte est en période probatoire.'
};

const userRestrictionService = {
  
  /**
   * Récupère toutes les restrictions de l'utilisateur connecté
   * @returns {Promise<Object>} Données de restrictions
   */
  async getMyRestrictions() {
    try {
      const response = await getWithFallback(
        '/users/me/restrictions',
        '/public/users/me/restrictions'
      );
      return response.data.data;
    } catch (error) {
      console.error('Erreur récupération restrictions:', error);
      throw error;
    }
  },
  
  /**
   * Vérifie rapidement le statut de ban
   * @returns {Promise<Object>} Statut de ban
   */
  async checkBanStatus() {
    try {
      const response = await getWithFallback(
        '/users/me/ban-status',
        '/public/users/me/ban-status'
      );
      return response.data.data;
    } catch (error) {
      console.error('Erreur vérification ban:', error);
      throw error;
    }
  },
  
  /**
   * Vérifie si une action spécifique est autorisée
   * @param {string} action - L'action à vérifier
   * @param {string} scope - Le scope optionnel
   * @param {string} scopeId - L'ID du scope optionnel
   * @returns {Promise<Object>} Résultat de la vérification
   */
  async checkRestriction(action, scope = null, scopeId = null) {
    try {
      const params = { action };
      if (scope) params.scope = scope;
      if (scopeId) params.scopeId = scopeId;
      
      const response = await getWithFallback(
        '/users/me/restriction-check',
        '/public/users/me/restriction-check',
        { params }
      );
      return response.data.data;
    } catch (error) {
      console.error('Erreur vérification restriction:', error);
      throw error;
    }
  },
  
  /**
   * Vérifie si l'utilisateur peut réserver
   * @returns {Promise<boolean>}
   */
  async canBook() {
    const result = await this.checkRestriction('book');
    return result.allowed;
  },
  
  /**
   * Vérifie si l'utilisateur peut créer une annonce
   * @returns {Promise<boolean>}
   */
  async canCreateListing() {
    const result = await this.checkRestriction('create_listing');
    return result.allowed;
  },
  
  /**
   * Vérifie si l'utilisateur peut envoyer des messages
   * @returns {Promise<boolean>}
   */
  async canSendMessage() {
    const result = await this.checkRestriction('send_message');
    return result.allowed;
  },
  
  /**
   * Vérifie si l'utilisateur peut laisser un avis
   * @returns {Promise<boolean>}
   */
  async canReview() {
    const result = await this.checkRestriction('review');
    return result.allowed;
  },
  
  /**
   * Récupère le label d'un type de restriction
   * @param {string} type - Type de restriction
   * @returns {string} Label traduit
   */
  getRestrictionLabel(type) {
    return RESTRICTION_LABELS[type] || type;
  },
  
  /**
   * Récupère la description d'un type de restriction
   * @param {string} type - Type de restriction
   * @returns {string} Description
   */
  getRestrictionDescription(type) {
    return RESTRICTION_DESCRIPTIONS[type] || '';
  },
  
  /**
   * Formate une date d'expiration de façon conviviale
   * @param {string} dateString - Date ISO
   * @returns {string} Date formatée
   */
  formatExpirationDate(dateString) {
    if (!dateString) return 'Indéfini';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = date - now;
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Expirée';
    if (diffDays === 0) return 'Expire aujourd\'hui';
    if (diffDays === 1) return 'Expire demain';
    if (diffDays < 7) return `Expire dans ${diffDays} jours`;
    if (diffDays < 30) return `Expire dans ${Math.ceil(diffDays / 7)} semaine(s)`;
    
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  },
  
  /**
   * Détermine la sévérité d'une restriction pour l'UI
   * @param {string} type - Type de restriction
   * @returns {string} 'low' | 'medium' | 'high' | 'critical'
   */
  getRestrictionSeverity(type) {
    const severityMap = {
      [RESTRICTION_TYPES.PROBATION]: 'low',
      [RESTRICTION_TYPES.SEARCH_DELIST]: 'low',
      [RESTRICTION_TYPES.KYC_HOLD]: 'medium',
      [RESTRICTION_TYPES.FEATURE_BLOCK]: 'medium',
      [RESTRICTION_TYPES.REVIEW_BLOCK]: 'medium',
      [RESTRICTION_TYPES.MESSAGE_BLOCK]: 'medium',
      [RESTRICTION_TYPES.BOOKING_BLOCK]: 'high',
      [RESTRICTION_TYPES.LISTING_BLOCK]: 'high',
      [RESTRICTION_TYPES.LISTING_UNPUBLISH]: 'high',
      [RESTRICTION_TYPES.PAYOUT_HOLD]: 'high',
      [RESTRICTION_TYPES.PAYIN_BLOCK]: 'high',
      [RESTRICTION_TYPES.BAN_TEMPORARY]: 'critical',
      [RESTRICTION_TYPES.BAN_PERMANENT]: 'critical'
    };
    
    return severityMap[type] || 'medium';
  },
  
  /**
   * Retourne la couleur MUI correspondant à la sévérité
   * @param {string} severity - Niveau de sévérité
   * @returns {string} Couleur MUI
   */
  getSeverityColor(severity) {
    const colorMap = {
      'low': 'info',
      'medium': 'warning',
      'high': 'error',
      'critical': 'error'
    };
    
    return colorMap[severity] || 'warning';
  },
  
  /**
   * Calcule les jours restants avant expiration
   * @param {string} expiresAt - Date d'expiration ISO
   * @returns {number|null} Nombre de jours ou null si permanent
   */
  getDaysRemaining(expiresAt) {
    if (!expiresAt) return null;
    
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diffMs = expiry - now;
    
    if (diffMs < 0) return 0;
    
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  }
};

export default userRestrictionService;
